import { Controller } from "@hotwired/stimulus"

// Portfolio share-link modal: lists existing links, creates new ones,
// revokes via a styled confirmation modal. Filters for new links are
// pulled from the current URL's query parameters at connect-time.

export default class extends Controller {
  static targets = [
    "formErrors", "listErrors",
    "nameInput", "expirySelect", "passwordInput", "financialsCheckbox",
    "submitBtn", "resetBtn",
    "generatedSection", "generatedUrl", "copyBtn",
    "list", "listLoading", "listEmpty", "listCount", "rowTemplate",
    "revokeMessage"
  ]
  static values = {
    indexUrl: String,
    createUrl: String,
    revokeUrl: String,
    currentFilters: Object
  }

  connect() {
    this._pendingRevoke = null
    this._load()
  }

  // ── Load existing links ─────────────────────────────────────

  async _load() {
    this.listLoadingTarget.classList.remove("hidden")
    this.listTarget.classList.add("hidden")
    this.listEmptyTarget.classList.add("hidden")
    this._clearListErrors()

    try {
      const res = await fetch(this.indexUrlValue, { headers: { Accept: "application/json" } })
      const data = await res.json()
      if (!res.ok) {
        this._showListErrors(data.errors || ["Failed to load share links"])
        return
      }
      this._renderList(data.links || [])
    } catch (err) {
      this._showListErrors([err.message || "Network error"])
    } finally {
      this.listLoadingTarget.classList.add("hidden")
    }
  }

  _renderList(links) {
    this.listTarget.innerHTML = ""
    this.listCountTarget.textContent = `${links.length} total`

    if (!links.length) {
      this.listEmptyTarget.classList.remove("hidden")
      return
    }

    links.forEach(link => this.listTarget.appendChild(this._buildRow(link)))
    this.listTarget.classList.remove("hidden")
  }

  _buildRow(link) {
    const node = this.rowTemplateTarget.content.firstElementChild.cloneNode(true)
    node.dataset.id = link.id
    node.dataset.link = JSON.stringify(link)

    node.querySelector("[data-role='name']").textContent = link.name || "Untitled link"
    node.querySelector("[data-role='filter-summary']").textContent = link.filter_summary || "All projects"
    node.querySelector("[data-role='meta']").textContent = metaLine(link)

    if (link.has_password) {
      const badge = node.querySelector("[data-role='password-badge']")
      badge.classList.remove("hidden")
      badge.classList.add("inline-flex")
    }
    if (!link.include_financials) node.querySelector("[data-role='financials-badge']").classList.remove("hidden")

    const expiredBadge = node.querySelector("[data-role='expired-badge']")
    const revokedBadge = node.querySelector("[data-role='revoked-badge']")
    const copyBtn = node.querySelector("[data-role='copy-btn']")
    const revokeBtn = node.querySelector("[data-role='revoke-btn']")

    if (link.revoked_at) {
      revokedBadge.classList.remove("hidden")
      node.classList.add("opacity-60")
      copyBtn.disabled = true
      copyBtn.classList.add("opacity-40", "cursor-not-allowed")
      revokeBtn.classList.add("hidden")
    } else if (!link.active && link.expires_at) {
      expiredBadge.classList.remove("hidden")
      node.classList.add("opacity-60")
    }

    return node
  }

  // ── Create ──────────────────────────────────────────────────

  async create() {
    this._clearFormErrors()
    this._setSubmitting(true)

    const body = new FormData()
    body.append("name", this.nameInputTarget.value.trim())
    body.append("expiry", this.expirySelectTarget.value)
    const password = this.passwordInputTarget.value
    if (password) body.append("password", password)
    body.append("include_financials", this.financialsCheckboxTarget.checked ? "true" : "false")

    const filters = this.currentFiltersValue || {}
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== "") {
        body.append(`filters[${key}]`, value)
      }
    })

    try {
      const res = await fetch(this.createUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": this._csrf(), Accept: "application/json" },
        body
      })
      const data = await res.json()
      if (!res.ok || !data.success) {
        this._showFormErrors(data.errors || ["Failed to create link"])
        return
      }

      this._showGeneratedUrl(data.link.url)
      this._showToast("Share link created", "notice")
      this._load()
    } catch (err) {
      this._showFormErrors([err.message || "Network error"])
    } finally {
      this._setSubmitting(false)
    }
  }

  _showGeneratedUrl(url) {
    this.generatedUrlTarget.value = url
    this.generatedSectionTarget.classList.remove("hidden")
    this.resetBtnTarget.classList.remove("hidden")
    this.submitBtnTarget.classList.add("hidden")
  }

  resetForm() {
    this.nameInputTarget.value = ""
    this.expirySelectTarget.value = "7d"
    this.passwordInputTarget.value = ""
    this.financialsCheckboxTarget.checked = true
    this.generatedSectionTarget.classList.add("hidden")
    this.resetBtnTarget.classList.add("hidden")
    this.submitBtnTarget.classList.remove("hidden")
    this._clearFormErrors()
  }

  // ── Copy ────────────────────────────────────────────────────

  copyUrl() {
    this._copy(this.generatedUrlTarget.value, this.copyBtnTarget)
  }

  copyRowUrl(event) {
    const row = event.currentTarget.closest("li[data-id]")
    if (!row) return
    const link = JSON.parse(row.dataset.link || "null")
    if (!link || !link.url || link.revoked_at) return
    this._copy(link.url, event.currentTarget)
  }

  async _copy(text, btn) {
    if (!text) return
    try {
      await navigator.clipboard.writeText(text)
      this._flashCopied(btn)
      this._showToast("URL copied", "notice")
    } catch (_err) {
      this._showToast("Couldn't copy — select and Cmd+C", "alert")
    }
  }

  _flashCopied(btn) {
    if (!btn) return
    const original = btn.innerHTML
    btn.innerHTML = `<span class="text-[13px]">Copied ✓</span>`
    setTimeout(() => { btn.innerHTML = original }, 1400)
  }

  // ── Revoke ──────────────────────────────────────────────────

  confirmRevoke(event) {
    const row = event.currentTarget.closest("li[data-id]")
    if (!row) return
    const link = JSON.parse(row.dataset.link || "null")
    if (!link) return
    this._pendingRevoke = link.id
    this.revokeMessageTarget.textContent =
      `"${link.name || "Untitled link"}" — anyone holding this URL will lose access. This can't be undone.`
    document.dispatchEvent(new CustomEvent("modal:open-by-id", { detail: { id: "portfolio-share-revoke" } }))
  }

  async doRevoke() {
    const id = this._pendingRevoke
    if (!id) return
    try {
      const res = await fetch(`${this.revokeUrlValue}/${id}`, {
        method: "DELETE",
        headers: { "X-CSRF-Token": this._csrf(), Accept: "application/json" }
      })
      const data = await res.json()
      if (!res.ok || !data.success) {
        this._showToast((data.errors || ["Revoke failed"]).join(", "), "alert")
        return
      }
      this._showToast("Share link revoked", "notice")
      this._closeModal("portfolio-share-revoke-modal")
      this._pendingRevoke = null
      this._load()
    } catch (err) {
      this._showToast(err.message || "Network error", "alert")
    }
  }

  // ── Helpers ─────────────────────────────────────────────────

  _csrf() {
    return document.querySelector("meta[name='csrf-token']")?.content
  }

  _setSubmitting(flag) {
    this.submitBtnTarget.disabled = flag
    this.submitBtnTarget.textContent = flag ? "Creating…" : "Create link"
  }

  _clearFormErrors() {
    this.formErrorsTarget.classList.add("hidden")
    this.formErrorsTarget.textContent = ""
  }

  _showFormErrors(messages) {
    this.formErrorsTarget.classList.remove("hidden")
    this.formErrorsTarget.textContent = messages.join(" · ")
  }

  _clearListErrors() {
    this.listErrorsTarget.classList.add("hidden")
    this.listErrorsTarget.textContent = ""
  }

  _showListErrors(messages) {
    this.listErrorsTarget.classList.remove("hidden")
    this.listErrorsTarget.textContent = messages.join(" · ")
  }

  _closeModal(id) {
    const el = document.getElementById(id)
    if (!el) return
    el.classList.add("hidden")
    document.body.style.overflow = ""
  }

  _showToast(msg, type = "notice") {
    const toast = document.createElement("div")
    toast.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    toast.setAttribute("data-controller", "flash")
    toast.setAttribute("data-flash-dismiss-after-value", "5000")
    toast.textContent = msg
    document.body.prepend(toast)
  }
}

function metaLine(link) {
  const parts = []
  parts.push(`${link.view_count} view${link.view_count === 1 ? "" : "s"}`)
  if (link.last_viewed_at) {
    parts.push(`Last viewed ${formatDate(link.last_viewed_at)}`)
  }
  if (link.expires_at) {
    parts.push(`Expires ${formatDate(link.expires_at)}`)
  } else {
    parts.push("No expiration")
  }
  parts.push(`By ${link.creator_name}`)
  return parts.join(" · ")
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
  } catch (_e) {
    return iso
  }
}
